import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { Menu } from '@headlessui/react'
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline'

function FloatingItems({ anchor, children }: { anchor: RefObject<HTMLButtonElement>; children: ReactNode }) {
  const menu = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  useLayoutEffect(() => {
    const place = () => {
      if (!anchor.current || !menu.current) return
      const rect = anchor.current.getBoundingClientRect()
      const height = menu.current.offsetHeight
      const width = menu.current.offsetWidth
      const below = rect.bottom + 8
      setPosition({
        top: Math.max(8, below + height <= window.innerHeight - 8 ? below : rect.top - height - 8),
        left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [anchor])
  return createPortal(<Menu.Items ref={menu} static style={position} className="fixed z-[100] max-h-[calc(100vh-16px)] w-52 max-w-[calc(100vw-16px)] overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 text-left shadow-xl focus:outline-none dark:border-white/10 dark:bg-[#1b1c1f]">{children}</Menu.Items>, document.body)
}

export default function BackupActionMenu({ name, children }: { name: string; children: ReactNode }) {
  const anchor = useRef<HTMLButtonElement>(null)
  return <Menu as="div" className="inline-block text-left">{({ open }) => <>
    <Menu.Button ref={anchor} aria-label={`Actions for ${name}`} className="rounded-lg border border-gray-200 p-2 text-gray-500 dark:border-white/10 dark:text-gray-400"><EllipsisVerticalIcon className="h-5 w-5" /></Menu.Button>
    {open && <FloatingItems anchor={anchor}>{children}</FloatingItems>}
  </>}</Menu>
}
